import { FC, ReactNode, useId, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Area, AreaChart, Tooltip as RechartsTooltip, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { FinancialValue } from '@/shared/components/financial-values';
import { ChartTooltipContent } from '@/shared/components/tooltips/CustomRechartsUsageAnalyticsTooltip';
import ViewportTooltipPositioner from '@/shared/components/tooltips/ViewportTooltipPositioner';
import { dayjs } from '@/utilities';

import type { FinancialMetricSparklinePoint } from '../../types';
import { resolveFinancialMetricSparklineFlatDomain } from './series';

export interface FinancialMetricSparklineProps {
    label: string;
    metricLabel: string;
    points: FinancialMetricSparklinePoint[];
    valueFormatter?: (value: number) => ReactNode;
}

interface FinancialMetricSparklineTooltipPayloadItem {
    value?: number | string | readonly (number | string)[];
}

export interface FinancialMetricSparklineTooltipProps {
    active?: boolean;
    anchorElement?: HTMLElement | null;
    coordinate?: { x?: number; y?: number };
    label?: number | string;
    metricLabel: string;
    payload?: readonly FinancialMetricSparklineTooltipPayloadItem[];
    valueFormatter?: (value: number) => ReactNode;
}

const MAX_SPARKLINE_POINTS = 80;

const samplePoints = (points: FinancialMetricSparklinePoint[]) => {
    const finitePoints = points.filter((point) => Number.isFinite(point.value));

    if (finitePoints.length <= MAX_SPARKLINE_POINTS) {
        return finitePoints;
    }

    const step = (finitePoints.length - 1) / (MAX_SPARKLINE_POINTS - 1);

    return Array.from({ length: MAX_SPARKLINE_POINTS }, (_, index) => finitePoints[Math.round(index * step)]);
};

const tooltipValue = (value: FinancialMetricSparklineTooltipPayloadItem['value']) => {
    const scalar = Array.isArray(value) ? value[0] : value;
    const number = Number(scalar);

    return Number.isFinite(number) ? number : null;
};

const tooltipPeriodLabel = (label: FinancialMetricSparklineTooltipProps['label']) => {
    if (label === undefined || label === null || label === '') {
        return undefined;
    }

    const value = String(label);
    const date = dayjs(value);

    if (!date.isValid()) {
        return value;
    }

    return /^\d{4}-\d{2}-\d{2}$/.test(value) ? date.format('MMM D, YYYY') : date.format('MMM D, YYYY h:mm A');
};

export const FinancialMetricSparklineTooltip: FC<FinancialMetricSparklineTooltipProps> = ({
    active,
    anchorElement,
    coordinate,
    label,
    metricLabel,
    payload = [],
    valueFormatter,
}) => {
    const { token } = useAntDesignTheme();
    const value = tooltipValue(payload[0]?.value);

    if (!active || value === null) {
        return null;
    }

    const tooltip = (
        <ChartTooltipContent
            compact
            label={tooltipPeriodLabel(label)}
            rows={[
                {
                    color: token.blue5,
                    fill: token.blue1,
                    key: 'value',
                    label: metricLabel,
                    value: valueFormatter ? valueFormatter(value) : <FinancialValue value={value} />,
                },
            ]}
        />
    );

    return anchorElement && coordinate?.x !== undefined && coordinate.y !== undefined && typeof document !== 'undefined'
        ? createPortal(
              <ViewportTooltipPositioner
                  anchorElement={anchorElement}
                  coordinate={{ x: coordinate.x, y: coordinate.y }}
              >
                  {tooltip}
              </ViewportTooltipPositioner>,
              document.body,
          )
        : tooltip;
};

export const FinancialMetricSparkline: FC<FinancialMetricSparklineProps> = ({
    label,
    metricLabel,
    points,
    valueFormatter,
}) => {
    const { token } = useAntDesignTheme();
    const anchorRef = useRef<HTMLDivElement>(null);
    const gradientId = useId().replaceAll(':', '');
    const data = useMemo(
        () =>
            samplePoints(points).map((point, index) => ({
                ...point,
                tooltipLabel: point.date ?? `Observation ${index + 1}`,
            })),
        [points],
    );
    const flatDomain = useMemo(() => resolveFinancialMetricSparklineFlatDomain(data), [data]);

    if (data.length < 2) {
        return null;
    }

    return (
        <div
            aria-label={label}
            data-financial-metric-sparkline='true'
            ref={anchorRef}
            role='img'
            style={{ flex: '1 1 8rem', height: '3.25rem', minWidth: '4rem', width: '100%' }}
        >
            <ResponsiveContainer height='100%' width='100%'>
                <AreaChart data={data} margin={{ bottom: 2, left: 1, right: 1, top: 2 }}>
                    <defs>
                        <linearGradient id={gradientId} x1='0' x2='0' y1='0' y2='1'>
                            <stop offset='0%' stopColor={token.blue5} stopOpacity={0.22} />
                            <stop offset='100%' stopColor={token.blue5} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <XAxis dataKey='tooltipLabel' hide />
                    {flatDomain ? <YAxis domain={flatDomain} hide width={0} /> : null}
                    <RechartsTooltip
                        allowEscapeViewBox={{ x: false, y: true }}
                        content={({ active, coordinate, label: tooltipLabel, payload }) => (
                            <FinancialMetricSparklineTooltip
                                active={active}
                                anchorElement={anchorRef.current}
                                coordinate={coordinate}
                                label={
                                    typeof tooltipLabel === 'string' || typeof tooltipLabel === 'number'
                                        ? tooltipLabel
                                        : undefined
                                }
                                metricLabel={metricLabel}
                                payload={payload}
                                valueFormatter={valueFormatter}
                            />
                        )}
                        cursor={{ stroke: token.colorBorder }}
                        isAnimationActive={false}
                        wrapperStyle={{ outline: 'none', pointerEvents: 'none', zIndex: 2 }}
                    />
                    <Area
                        activeDot={{
                            fill: token.blue5,
                            r: 4,
                            stroke: token.colorBgContainer,
                            strokeWidth: 2,
                        }}
                        baseValue={flatDomain ? 'dataMin' : undefined}
                        dataKey='value'
                        fill={`url(#${gradientId})`}
                        fillOpacity={1}
                        isAnimationActive={false}
                        stroke={token.blue5}
                        strokeWidth={1.75}
                        type='monotone'
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};
